/**
 * Transaction recovery state — populated when StationPage mounts. Sits next
 * to useStationStore rather than inside it because the two have different
 * lifecycles: recovery is a one-shot prompt at load time, whereas station
 * state is continuous during the session.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { TransactionRepositoryDexie } from '@/persistence/dexie/repositories';
import { RecoveryState, type StationId, type TransactionId } from '@/domain/types';
import type { TransactionSnapshot } from '@/domain';

export interface RecoveryStoreState {
  readonly stationId: StationId | null;
  readonly open: readonly TransactionSnapshot[];
  readonly status: 'idle' | 'scanned' | 'resolved';
}

export interface RecoveryStoreActions {
  scan(stationId: StationId): Promise<readonly TransactionSnapshot[]>;
  resume(id: TransactionId): Promise<void>;
  clearStale(id: TransactionId): Promise<void>;
  clearAllStale(): Promise<void>;
  decideLater(): void;
}

export type RecoveryStore = RecoveryStoreState & RecoveryStoreActions;

const repo = new TransactionRepositoryDexie(db);

export const useRecoveryStore = create<RecoveryStore>((set, get) => ({
  stationId: null,
  open: [],
  status: 'idle',

  async scan(stationId: StationId): Promise<readonly TransactionSnapshot[]> {
    const open = await repo.findOpen(stationId);
    set({ stationId, open, status: 'scanned' });
    return open;
  },

  async resume(id: TransactionId): Promise<void> {
    const { stationId, open } = get();
    if (!stationId) return;
    const snap = open.find((t) => t.id === id);
    if (!snap) return;
    await repo.upsert(stationId, { ...snap, recoveryState: RecoveryState.Adopted });
    set({ open: open.filter((t) => t.id !== id) });
    if (get().open.length === 0) set({ status: 'resolved' });
  },

  async clearStale(id: TransactionId): Promise<void> {
    const { stationId, open } = get();
    if (!stationId) return;
    const snap = open.find((t) => t.id === id);
    if (!snap) return;
    await repo.upsert(stationId, { ...snap, recoveryState: RecoveryState.Orphaned });
    set({ open: open.filter((t) => t.id !== id) });
    if (get().open.length === 0) set({ status: 'resolved' });
  },

  async clearAllStale(): Promise<void> {
    const { stationId, open } = get();
    if (!stationId) return;
    for (const snap of open) {
      await repo.upsert(stationId, { ...snap, recoveryState: RecoveryState.Orphaned });
    }
    set({ open: [], status: 'resolved' });
  },

  decideLater(): void {
    set({ status: 'resolved' });
  },
}));
